import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, distinctUntilChanged, map, Observable, tap } from 'rxjs';
import { AuthUser } from '../../models/auth-user.model';
import { TokenService } from '../token.service';
import { LocalStorageService } from '@core/services/local-storage.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService extends ApiService {

    private currentUserSubject: BehaviorSubject<AuthUser | null> = new BehaviorSubject<AuthUser | null>(null);
    currentUser$: Observable<AuthUser | null> = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

    isAuthenticated$: Observable<boolean> = this.currentUser$.pipe(map((user) => !!user));

    get currentUser() {
        return this.currentUserSubject.value;
    }

    get isAuthenticated() {
        return this.currentUserSubject.value !== null;
    }

    login(credentials: { email: string, password: string }) {
        return super.post('/auth/login', credentials)
            .pipe(tap(({data}) => {
                if (data?.token && data?.user) {
                    this.setAuth(data);
                }
            }));
    }

    verifyTwoFactor(body: { challenge_token: string, code: string }) {
        return super.post('/auth/two-factor/verify', body)
            .pipe(tap(({data}) => this.setAuth(data)));
    }

    resendTwoFactor(body: { challenge_token: string }) {
        return super.post('/auth/two-factor/resend', body);
    }

    forgot(body: { email: string }) {
        return super.post('/auth/forgot', body);
    }

    reset(body: any) {
        return super.post('/auth/reset', body);
    }

    logout() {
        return super.post('/auth/logout')
            .pipe(tap(() => this.purgeAuth()));
    }

    me(): Observable<any> {
        return super.get('/auth/me').pipe(
            tap({
                next: ({data}) => this.setAuth({user: data, token: undefined }),
                error: () => this.purgeAuth(),
            }),
        );
    }

    setAuth(data: { user: AuthUser, token?: string }): void {
        this.currentUserSubject.next(data.user);
        if (data.token !== undefined) {
            (new TokenService()).saveToken(data.token);
        }
    }

    purgeAuth(): void {
        this.currentUserSubject.next(null);
        (new TokenService()).destroyToken();
        (new LocalStorageService()).clearUser();
    }

    isLockBySelf(lockedBy: any | null) {
        if (!lockedBy) return false;

        return lockedBy?.id === this.currentUser?.id;
    }

    isLockByOther(lockedBy: any | null) {
        if (!lockedBy) return false;

        return lockedBy?.id !== this.currentUser?.id;
    }

    hasAnyRole(roles: string|string[]): boolean {
        if (!Array.isArray(roles)) {
            roles = [roles];
        }

        const userRoles = this.currentUser?.roles ?? [];
        const list = Array.isArray(userRoles) ? userRoles : [];

        return list.some((role) => roles.includes(role));
    }
}
